import { action, makeObservable, observable } from 'mobx';
import moment from 'moment';

import epaxios from '@/utils/epaxios';
import RootStore from './RootStore';
import { permissions } from '@/constants';
import { onCurrentStationChange } from '@/utils/store';

/*
* Backend endpoint:
  Expected payload:
  [
	{
		month:number (1-12)
		devices: [
			{
				deviceId:string,
				maxFlow: number,
				flowDegradation:number
			}
		],
		peakSupplyFlow: number;
        averageSupplyFlow: number;
	}
]

* */

export default class StationRiskGraphStore {
  @observable stationRiskGraphData: StationRiskGraphData;
  @observable isLoading = false;
  @observable isReady = false;
  @observable lastPage = 0;
  @observable yearlyViewOpen = false;
  @observable graphsByYears: Record<number, StationRiskGraphData> = {};

  constructor(private rootStore: typeof RootStore) {
    makeObservable(this);
    onCurrentStationChange(this, () => this.graphsByYears = {})
  }

  @action setYearlyViewOpen = (isOpen: boolean) => {
    this.yearlyViewOpen = isOpen;
  };

  @action resetGraphOnYearChange = () => {
    this.stationRiskGraphData = undefined;
  };

  @action getGraphByYear = async (year: number) => {
    if (this.isLoading) return;
    try {
      if (this.rootStore.permissionStore.isAuth(permissions.VIEW_STATION_VALUE_DASHBOARD_STATION_RISK)) {
        this.resetGraph();
        if (this.graphsByYears[year]) {
          this.stationRiskGraphData = this.graphsByYears[year];
          this.isReady = true;
          return;
        }
        this.isLoading = true;
        const stationId = this.rootStore.stationStore?.currentStation.id;
        const yearOffset = Number(moment().year()) - year;
        const { data } = await epaxios.get(
          `/value-dashboard/station-risk/station/${stationId}/yearly-view/${yearOffset}`,
        );
        if (data?.validationErrorName) {
          throw new Error(data.msg);
        }
        this.stationRiskGraphData = new StationRiskGraphData(data);
        this.graphsByYears[year] = this.stationRiskGraphData;
        this.isReady = true;
      }
    } catch (e) {
      this.stationRiskGraphData = undefined;
    } finally {
      this.isLoading = false;
    }
  };

  @action resetGraph = () => {
    this.stationRiskGraphData = undefined;
    this.isLoading = false;
    this.isReady = false;
    this.lastPage = 0;
  };
}

class StationRiskGraphData {
  @observable data = [];
  @observable plannedAvailabilitySeries = [];
  @observable peakProductionSeries = [];
  @observable avgSupplyFlowSeries = [];
  @observable degradationSeries = [];
  @observable highRiskSeries = [];
  @observable moderateRiskSeries = [];
  @observable healthyRangeSeries = [];

  constructor(data) {
    makeObservable(this);
    this.data = data;
    this.addDegradationToData();
    this.sortDevicesByFlowAfterDegradation();
    this.setSeriesData();
  }

  addDegradationToData() {
    this.data.forEach(monthData => {
      --monthData.month;
      let sumMonthlyDegradation: number;
      let sumMonthMaxFlow = 0;
      monthData.devices.forEach(device => {
        device['flowAfterDegradation'] = device.maxFlow - device.flowDegradation;
        sumMonthlyDegradation = (sumMonthlyDegradation || 0 ) + device.flowDegradation;
        sumMonthMaxFlow += device.maxFlow;
      });
      monthData['maxFlow'] = sumMonthMaxFlow;
      monthData['flowAfterDegradation'] = sumMonthMaxFlow - sumMonthlyDegradation;
      monthData['totalDegradation'] = sumMonthlyDegradation;
    });
  }

  sortDevicesByFlowAfterDegradation() {
    this.data.forEach(month => {
      month.devices.replace(
        month.devices.slice().sort((device1, device2) => device1.flowAfterDegradation - device2.flowAfterDegradation),
      );
    });
  }

  @action setSeriesData() {
    this.data.forEach(monthData => {
      this.plannedAvailabilitySeries.push({
        x: monthData.month,
        y: monthData.maxFlow,
      });

      this.peakProductionSeries.push({
        x: monthData.month,
        y: monthData.peakSupplyFlow,
      });

      this.avgSupplyFlowSeries.push({
        x: monthData.month,
        y: monthData.averageSupplyFlow,
      });

      this.degradationSeries.push({
        x: monthData.month,
        y: monthData.totalDegradation,
      });

      this.highRiskSeries.push({
        x: monthData.month,
        y: monthData.devices[0]?.flowAfterDegradation || 0,
      });

      this.moderateRiskSeries.push({
        x: monthData.month,
        y: monthData.devices[1]?.flowAfterDegradation || 0,
      });

      this.healthyRangeSeries.push({
        x: monthData.month,
        y:
          monthData.flowAfterDegradation -
          monthData.devices[0]?.flowAfterDegradation || 0 -
          (monthData.devices[1]?.flowAfterDegradation || 0),
      });
    });
  }
}
